import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { Activity, CheckCircle2, CircleAlert } from 'lucide-react';
import { STRAVA_STATUS_QUERY } from '../graphql/queries';
import { DISCONNECT_STRAVA_MUTATION, START_STRAVA_CONNECT_MUTATION } from '../graphql/mutations';
import { ConfirmDialog } from './ConfirmDialog';

export interface StravaStatus {
  available: boolean;
  connected: boolean;
  athleteName: string | null;
  lastActivityAt: string | null;
}

interface StravaStatusData {
  stravaStatus: StravaStatus;
}

/** "today at 07:12", "Jul 8 at 09:12" — calm, local. */
function friendlyActivityAt(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (date.toDateString() === new Date().toDateString()) return `today at ${time}`;
  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at ${time}`;
}

/** The calm message for a `?strava=` marker the OAuth callback redirected with. */
function callbackMessage(marker: string | null): string | null {
  if (marker === 'denied') return 'Connecting was cancelled on Strava. Nothing changed.';
  if (marker === 'error') return 'Connecting to Strava did not complete. Please try again.';
  return null;
}

/**
 * "Strava" — the connect-your-activities card on Profile. Once connected, each
 * new Strava activity appends one moment line (title, time, pace, heart rate)
 * to that day's journal document. States: not configured (server keeps the
 * feature off) → the card hides; unconnected → intro + connect button (a full
 * OAuth redirect to Strava); connected → athlete + last activity + disconnect.
 */
export function StravaCard() {
  const { data, loading, error, refetch } = useQuery<StravaStatusData>(STRAVA_STATUS_QUERY);
  const [searchParams] = useSearchParams();
  const [connectError, setConnectError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [startConnect, { loading: connecting }] = useMutation<{ startStravaConnect: string }>(
    START_STRAVA_CONNECT_MUTATION,
    {
      onError: () =>
        setConnectError('Could not start connecting to Strava. Please try again shortly.'),
      onCompleted: (result) => {
        // The rest of the flow lives on Strava's site; the OAuth callback
        // brings the browser back to this page with a ?strava= marker.
        window.location.assign(result.startStravaConnect);
      },
    }
  );
  const [disconnect, { loading: disconnecting }] = useMutation(DISCONNECT_STRAVA_MUTATION, {
    onCompleted: () => void refetch(),
    onError: () => void refetch(),
  });

  // The feature is off on this deployment, or the status can't load — either
  // way this stays a quiet non-surface rather than an error card.
  if (error) return null;
  if (data && !data.stravaStatus.available) return null;

  const status = data?.stravaStatus;
  const calloutMessage = connectError ?? callbackMessage(searchParams.get('strava'));
  const lastActivity = friendlyActivityAt(status?.lastActivityAt ?? null);

  return (
    <section
      aria-label="Strava"
      className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
    >
      <div className="flex items-center gap-3 mb-1">
        <Activity className="w-5 h-5 text-[var(--text-secondary)] shrink-0" aria-hidden />
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Strava</h2>
      </div>

      {loading && <span className="skeleton block h-12 w-full rounded-xl" aria-hidden />}

      {!loading && status && !status.connected && (
        <>
          <p className="text-sm text-[var(--text-secondary)]">
            Bring your workouts into your journal. Each activity you record on Strava adds a line to
            that day&apos;s entries — what it was, when, your pace, your heart rate.
          </p>
          {calloutMessage && (
            <p role="alert" className="mt-3 flex items-start gap-2 text-sm text-amber-700">
              <CircleAlert className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
              <span>{calloutMessage}</span>
            </p>
          )}
          <button
            type="button"
            className="btn btn-primary mt-4"
            onClick={() => {
              setConnectError(null);
              void startConnect();
            }}
            disabled={connecting}
            aria-busy={connecting}
          >
            {connecting ? 'Opening Strava…' : 'Connect Strava'}
          </button>
        </>
      )}

      {!loading && status?.connected && (
        <div className="mt-2 space-y-3">
          <p className="flex items-start gap-2 text-sm text-[var(--text-secondary)]" role="status">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" aria-hidden />
            <span>
              Connected{status.athleteName ? ` as ${status.athleteName}` : ''}
              {lastActivity ? ` — last activity added ${lastActivity}` : ''}
            </span>
          </p>
          <p className="text-sm text-[var(--text-tertiary)]">
            New activities appear in your journal on the day they happened.
          </p>
          <button
            type="button"
            className="btn btn-ghost text-red-600"
            onClick={() => setConfirmOpen(true)}
            disabled={disconnecting}
            aria-busy={disconnecting}
          >
            {disconnecting ? 'Disconnecting…' : 'Disconnect'}
          </button>
          <ConfirmDialog
            open={confirmOpen}
            title="Disconnect Strava?"
            body="New activities stop appearing in your journal. Everything already written stays exactly as it is. You can reconnect at any time."
            confirmLabel="Disconnect"
            onConfirm={() => {
              setConfirmOpen(false);
              void disconnect();
            }}
            onCancel={() => setConfirmOpen(false)}
          />
        </div>
      )}
    </section>
  );
}
